/**
 * The map view: the whole working area given to the map.
 *
 * No new data and no new requests — the map is the desk's, given all the room. What changes
 * is which question the screen answers: on the desk it is "what is waiting", here it is
 * only "where is everybody".
 *
 * Nothing sits beside it. The telephone, the order form, the queue and the counts are the
 * desk's, one key away; a column of them here took a third of the screen from the one thing
 * this view is opened for, on the machine left showing the city on a wall.
 */

import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import { OrderDetail } from '../orders/OrderDetail';
import { useQueue } from '../orders/useQueue';
import { Card, EmptyState } from '../ui';

const DriverMap = lazy(async () => ({ default: (await import('../map/DriverMap')).DriverMap }));

export function MapView() {
  const { t } = useTranslation();
  const { orders, selected, close, cancelled, refresh, reload } = useQueue();

  return (
    <div className="flex flex-col p-3 flex-1 min-h-0">
      <Suspense
        fallback={
          <Card className="flex flex-col flex-1 min-h-0">
            <EmptyState>{t('app.loading')}</EmptyState>
          </Card>
        }
      >
        <DriverMap selectedOrder={selected} className="flex-1">
          {/* The order opened on the desk, when there is one — pressing `2` to see where its
              driver is must not lose it. Bottom-left with 16px insets, deliberately:
              MapLibre's own controls and its attribution sit in the bottom-right of the
              same box. */}
          {selected !== null && (
            <OrderDetail
              order={selected}
              onClose={close}
              onChanged={() => void reload()}
              onCancelled={cancelled}
              /* Whether the automation still has it is dispatch's state rather than the
                 order's, so it comes from the queue this view is already holding. The queue
                 is what the button reads its pressed state back from, which is why toggling
                 refreshes that rather than the order. */
              automatic={orders.find((o) => o.orderId === selected.id)?.auto.automatic}
              onAutomaticChanged={() => void refresh()}
              candidateLimit={2}
              /* 548px is the width this card has on the desk; 45% keeps it from covering
                 most of a tablet's map. At 380px Cancel was drawn over even the shortest
                 state; now the header fits on one line for most states, and the longest
                 wrap their buttons onto a second. */
              className="absolute bottom-4 left-4 w-[min(548px,45%)] max-h-[calc(100%-2rem)] overflow-y-auto shadow-float"
            />
          )}
        </DriverMap>
      </Suspense>
    </div>
  );
}
